import {
    Add, Delete, Download, FileCopy, MoreVert,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    InputLabel,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tab,
    Tabs,
    Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { DEFAULT_CULTURE } from 'src/constants';
import { config } from 'src/lib/config';

export interface GroupedLocales {
    [region: string]: string[];
}

interface RankingTabsProps {
    selectedVariant: number;
    variants: Models.IngestConfigApi.ForcedRankings.CultureVariantRanking[];
    onVariantChange: (index: number) => void;
    onVariantAdd: () => void;
    onVariantDelete: (index: number) => void;
    onUpdateVariantCultures: (variantIndex: number, cultures: string[]) => void;
    onShowAddRow: () => void;
    onVariantClone: (index: number) => void;
    disableAddVariant?: boolean;
}

const { groupedLocales } = config as unknown as { groupedLocales: GroupedLocales };

export const RankingTabs = ({
    variants,
    selectedVariant,
    onVariantChange,
    onVariantAdd,
    onVariantDelete,
    onUpdateVariantCultures,
    onShowAddRow,
    onVariantClone,
    disableAddVariant,
}: RankingTabsProps): JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleSettingsClick = (event: React.MouseEvent<HTMLElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    const handleVariantChange = (_: React.SyntheticEvent, newValue: number): void => {
        onVariantChange(newValue);
    };

    const handleCultureAdd = (event: SelectChangeEvent<string>): void => {
        const culture = event.target.value;

        if (culture && !variants[selectedVariant].cultures.includes(culture)) {
            const updatedCultures = [...variants[selectedVariant].cultures, culture];

            onUpdateVariantCultures(selectedVariant, updatedCultures);
        }
    };

    const selectedCultures = variants[selectedVariant].cultures;
    const disableDeleteVariant = variants.length === 1;
    const selectedVariantHasNoRanks = variants[selectedVariant].rankings.length === 0;
    const hasNoCultureSelected = selectedCultures.length === 0;

    const handleCultureToggle = (culture: string): void => {
        const isSelected = selectedCultures.includes(culture);

        const newCultures = isSelected
            ? selectedCultures.filter((c) => c !== culture)
            : [...selectedCultures, culture];

        onUpdateVariantCultures(selectedVariant, newCultures);
    };

    const handleExportVariant = (): void => {
        const currentVariant = variants[selectedVariant];
        const designIds = currentVariant.rankings.map((ranking) => ranking.designConceptId);

        const csvContent = [
            'Design Concept ID',
            ...designIds,
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `variant-${selectedVariant + 1}-export.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        handleClose();
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
            }}
            >
                <Tabs value={selectedVariant} onChange={handleVariantChange}>
                    {variants?.map((variant, index) => {
                        let tabLabel = `Variant ${index + 1}`;

                        if (variant.cultures?.length > 0) {
                            if (variant.cultures.includes(DEFAULT_CULTURE)) {
                                tabLabel = `${DEFAULT_CULTURE}`;
                            } else {
                                tabLabel = `${variant.cultures[0]}`;
                            }

                            if (variant.cultures.length > 1) {
                                tabLabel = `${tabLabel} (${variant.cultures.length})`;
                            }
                        }

                        return (
                            <Tab
                                key={variant.name}
                                label={tabLabel}
                            />
                        );
                    })}
                </Tabs>
                <Tooltip
                    arrow
                    title={disableAddVariant
                        ? 'Cannot add another empty variant. Please fill the existing one first.'
                        : 'Add variant'}
                >
                    <span>
                        <IconButton
                            color="secondary"
                            disabled={disableAddVariant}
                            size="small"
                            sx={{
                                '& .MuiSvgIcon-root': {
                                    height: 20,
                                    width: 20,
                                },
                            }}
                            onClick={onVariantAdd}
                        >
                            <Add />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <FormControl
                    size="small"
                    sx={{
                        maxWidth: 300,
                        width: '100%',
                        '& .MuiOutlinedInput-root': hasNoCultureSelected ? {
                            borderColor: 'warning.main',
                            borderWidth: 2,
                            '&:hover': {
                                borderColor: 'warning.main',
                            },
                            '&.Mui-focused': {
                                borderColor: 'warning.main',
                            },
                        } : {},
                    }}
                >
                    <InputLabel
                        id="culture-select-label"
                        sx={hasNoCultureSelected ? { color: 'warning.main' } : {}}
                    >
                        {hasNoCultureSelected ? 'Select Culture (Required)' : 'Cultures'}
                    </InputLabel>
                    <Select
                        displayEmpty
                        id="culture-select"
                        label={hasNoCultureSelected ? 'Select Culture (Required)' : 'Cultures'}
                        labelId="culture-select-label"
                        renderValue={(): string => (selectedCultures.length > 0 ? selectedCultures.join(', ') : 'Select culture')}
                        value="culture-select"
                        onChange={handleCultureAdd}
                    >
                        {Object.entries(groupedLocales).flatMap(([region, locales]) => {
                            const selectedInGroup = locales.filter((locale) => selectedCultures.includes(locale));
                            const allSelected = selectedInGroup.length === locales.length;
                            const noneSelected = selectedInGroup.length === 0;
                            const someSelected = !allSelected && !noneSelected;
                            const regionDisabled = locales.every((locale) => variants.some(
                                (variant, index) => index !== selectedVariant
                                    && variant.cultures.includes(locale),
                            ));
                            const handleRegionToggle = (event: React.ChangeEvent<HTMLInputElement>): void => {
                                const { checked } = event.target;
                                const availableLocales = locales.filter(
                                    (locale) => !variants.some(
                                        (variant, index) => index !== selectedVariant
                                            && variant.cultures.includes(locale),
                                    ),
                                );
                                const selectedAvailable = availableLocales.filter(
                                    (locale) => selectedCultures.includes(locale),
                                );
                                const allAvailableSelected = selectedAvailable.length === availableLocales.length;
                                let newCultures: string[];

                                if (checked) {
                                    if (allAvailableSelected) {
                                        newCultures = selectedCultures.filter(
                                            (c) => !availableLocales.includes(c),
                                        );
                                    } else {
                                        newCultures = [
                                            ...new Set([
                                                ...selectedCultures,
                                                ...availableLocales,
                                            ]),
                                        ];
                                    }
                                } else {
                                    newCultures = selectedCultures.filter(
                                        (c) => !availableLocales.includes(c),
                                    );
                                }
                                onUpdateVariantCultures(selectedVariant, newCultures);
                            };

                            return [
                                <ListSubheader key={region} sx={{ pl: 1 }}>
                                    <Checkbox
                                        checked={allSelected}
                                        disabled={regionDisabled}
                                        indeterminate={someSelected}
                                        onChange={handleRegionToggle}
                                    />
                                    {region}
                                </ListSubheader>,
                                ...locales.map((locale: string) => {
                                    const isSelected = selectedCultures.includes(locale);
                                    const isDisabled = variants.some(
                                        (variant, _index) => _index !== selectedVariant
                                        && variant.cultures.includes(locale),
                                    );

                                    return (
                                        <MenuItem
                                            disabled={isDisabled}
                                            key={locale}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                            value={locale}
                                            onClick={(): void => handleCultureToggle(locale)}
                                        >
                                            <Checkbox checked={isSelected} />
                                            <span>{locale}</span>
                                        </MenuItem>
                                    );
                                }),
                            ];
                        })}
                    </Select>
                </FormControl>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton
                        aria-label="settings"
                        onClick={handleSettingsClick}
                    >
                        <MoreVert />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={handleClose}
                    >
                        <Tooltip
                            title={selectedVariantHasNoRanks
                                ? 'Cannot clone a variant with no ranks.'
                                : ''}
                        >
                            <span>
                                <MenuItem
                                    disabled={selectedVariantHasNoRanks}
                                    onClick={(): void => {
                                        onVariantClone(selectedVariant);
                                        handleClose();
                                    }}
                                >
                                    <ListItemIcon>
                                        <FileCopy fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>
                                        Clone Variant
                                    </ListItemText>
                                </MenuItem>
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={selectedVariantHasNoRanks ? 'Cannot export a variant with no ranks.' : 'Download a CSV with Design Concept IDs from this variant'}
                        >
                            <span>
                                <MenuItem
                                    disabled={selectedVariantHasNoRanks}
                                    onClick={handleExportVariant}
                                >
                                    <ListItemIcon>
                                        <Download fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>
                                        Export Variant
                                    </ListItemText>
                                </MenuItem>
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={disableDeleteVariant ? 'At least one variant is required.' : ''}
                        >
                            <span>
                                <MenuItem
                                    disabled={disableDeleteVariant}
                                    onClick={(): void => {
                                        onVariantDelete(selectedVariant);
                                        handleClose();
                                    }}
                                >
                                    <ListItemIcon>
                                        <Delete color="error" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primaryTypographyProps={{ color: 'error' }}>
                                        Delete Variant
                                    </ListItemText>
                                </MenuItem>
                            </span>
                        </Tooltip>
                    </Menu>
                    <Tooltip title={hasNoCultureSelected ? 'Select a culture first to add concepts' : ''}>
                        <span>
                            <Button
                                disabled={hasNoCultureSelected}
                                startIcon={<Add />}
                                sx={{ ml: 'auto' }}
                                variant="outlined"
                                onClick={onShowAddRow}
                            >
                                Add Concept
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
};
